import React from "react";
import { View, StyleSheet, TouchableOpacity, ActivityIndicator } from "react-native";
import { ArrowRightIcon } from "@phosphor-icons/react";

import { DegreesBySchoolByQueryData } from "@axio/graphql/generated/graphql";
import TableControlsContainer from "../../../../containers/general/TableControls/TableControlsContainer";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";

interface DegreesListProps {
  degreesData: DegreesBySchoolByQueryData;
  onDegreeSelect: (degreeId: string) => void;
  isLoading: boolean;
  hasError: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
}

const DegreesList: React.FC<DegreesListProps> = ({
  degreesData,
  onDegreeSelect,
  isLoading,
  hasError,
  page,
  setPage,
  pageSize,
  setPageSize,
}) => {
  if (hasError) {
    return (
      <View style={styles.container}>
        <CustomText text="Error loading degrees. Please try again." useTranslationText={false} />
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  if (degreesData.degrees.length === 0) {
    return (
      <View style={styles.container}>
        <CustomText text="No degree programs found. Please ensure that degree programs are created in the system." />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.degreeGrid}>
        {degreesData.degrees.map((degree) => (
          <TouchableOpacity key={degree.id} style={styles.degreeCard} onPress={() => onDegreeSelect(degree.id)}>
            <View style={styles.degreeCardContent}>
              <CustomText
                text={degree.name}
                weight="bold"
                size="l"
                useTranslationText={false}
                style={styles.degreeTitle}
              />

              {degree.description && (
                <CustomText
                  text={degree.description}
                  size="s"
                  useTranslationText={false}
                  style={styles.degreeDescription}
                />
              )}

              <CustomSpacing type="vertical" size="m" />

              <View style={styles.actionContainer}>
                <CustomText text="Tap to view details" size="s" style={styles.actionText} />
                <ArrowRightIcon size={12} color="#0084FF" />
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>
      {degreesData.totalCount > 0 && (
        <TableControlsContainer
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={degreesData.totalCount}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  container: {
    flex: 1,
  },
  degreeGrid: {
    // @ts-expect-error - works in web
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(300px, 1fr))",
    gap: 16,
  },
  degreeCard: {
    flex: 1,
    minWidth: 300,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e5e5",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  degreeCardContent: {
    padding: 16,
  },
  degreeTitle: {
    marginBottom: 8,
  },
  degreeDescription: {
    color: "#666",
    marginBottom: 12,
  },
  actionContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  actionText: {
    color: "#0084FF",
    fontStyle: "italic",
  },
});

export default DegreesList;
